import React, { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, KeyRound, Users, BarChart3, ArchiveRestore, LifeBuoy, Check, Pencil } from "lucide-react";
import { toast } from "sonner";

import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../state/auth";

export default function AdminPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [removed, setRemoved] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("team");
  const [openRequests, setOpenRequests] = useState(0);

  useEffect(() => {
    api
      .get("/auth/password-requests", { params: { status: "open" } })
      .then((r) => setOpenRequests(r.data.open_count || 0))
      .catch(() => setOpenRequests(0));
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/auth/users", { params: { include_removed: true } });
      setUsers(r.data.filter((u) => !u.deleted_at));
      setRemoved(r.data.filter((u) => u.deleted_at));
    } catch (e) {
      toast.error("Failed to load users", { description: formatApiErrorDetail(e?.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createUser = async (payload) => {
    try {
      await api.post("/auth/users", payload);
      toast.success("User created");
      load();
    } catch (e) {
      toast.error("Create failed", { description: formatApiErrorDetail(e?.response?.data?.detail) });
    }
  };

  const patchUser = async (id, patch) => {
    try {
      await api.patch(`/auth/users/${id}`, patch);
      toast.success("User updated");
      load();
      return true;
    } catch (e) {
      toast.error("Update failed", { description: formatApiErrorDetail(e?.response?.data?.detail) });
      return false;
    }
  };

  const deleteUser = async (id, email) => {
    if (
      !window.confirm(
        `Remove ${email}?\n\nTheir access is revoked immediately. The account is kept in "Removed accounts" below and can be restored at any time.`
      )
    )
      return;
    try {
      await api.delete(`/auth/users/${id}`);
      toast.success("User removed", { description: "You can restore this account from Removed accounts." });
      load();
    } catch (e) {
      toast.error("Remove failed", { description: formatApiErrorDetail(e?.response?.data?.detail) });
    }
  };

  const restoreUser = async (id, email) => {
    try {
      await api.post(`/auth/users/${id}/restore`);
      toast.success(`${email} restored`);
      load();
    } catch (e) {
      toast.error("Restore failed", { description: formatApiErrorDetail(e?.response?.data?.detail) });
    }
  };

  return (
    <div data-testid="admin-page" className="min-h-screen bg-white">
      <Header />
      <div className="max-w-5xl mx-auto px-6 lg:px-10 pt-12 pb-24">
        <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#c80f3f]">Administration</div>
        <h1 className="mt-2 font-display text-3xl sm:text-4xl font-bold text-[#1f2a44] tracking-tight">Team, access &amp; usage</h1>
        <p className="mt-3 text-sm text-[#4b5563] max-w-2xl leading-relaxed">
          Manage who can use the Specification Builder and see how the tool is
          actually being used — logins, specifications generated and downloads,
          per user.
        </p>

        <div className="mt-8 flex items-center gap-2 border-b border-[#e5e7eb]">
          {[
            { id: "team", label: "Team & access", icon: Users },
            { id: "usage", label: "Usage", icon: BarChart3 },
            {
              id: "requests",
              label: openRequests > 0 ? `Requests (${openRequests})` : "Requests",
              icon: LifeBuoy,
            },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium -mb-px border-b-2 transition-colors ${
                tab === t.id
                  ? "border-[#c80f3f] text-[#1f2a44]"
                  : "border-transparent text-[#4b5563] hover:text-[#1f2a44]"
              }`}
            >
              <t.icon className="h-4 w-4" strokeWidth={1.75} />
              {t.label}
            </button>
          ))}
        </div>

        {tab === "usage" && <UsageTab />}
        {tab === "requests" && <RequestsTab onCount={setOpenRequests} />}

        {tab === "team" && (
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <Card className="border border-[#e5e7eb] rounded-sm shadow-none">
              <div className="px-5 py-3 border-b border-[#e5e7eb] flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#4b5563]">
                  Users ({users.length})
                </span>
                {loading && <Loader2 className="h-3 w-3 animate-spin text-[#4b5563]" />}
              </div>
              <div className="divide-y divide-[#e5e7eb]">
                {users.map((u) => (
                  <UserRow
                    key={u.id}
                    u={u}
                    isSelf={u.id === user?.id}
                    onPatch={patchUser}
                    onDelete={deleteUser}
                  />
                ))}
                {!loading && users.length === 0 && (
                  <div className="p-6 text-sm text-[#4b5563] italic">No users yet.</div>
                )}
              </div>
            </Card>

            {removed.length > 0 && (
              <Card
                data-testid="removed-users-card"
                className="mt-6 border border-[#e5e7eb] rounded-sm shadow-none"
              >
                <div className="px-5 py-3 border-b border-[#e5e7eb] flex items-center gap-3">
                  <ArchiveRestore className="h-4 w-4 text-[#4b5563]" strokeWidth={1.75} />
                  <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#4b5563]">
                    Removed accounts ({removed.length})
                  </span>
                </div>
                <div className="divide-y divide-[#e5e7eb]">
                  {removed.map((u) => (
                    <div
                      key={u.id}
                      data-testid={`removed-user-row-${u.id}`}
                      className="px-5 py-4 flex items-center justify-between gap-4 flex-wrap"
                    >
                      <div>
                        <div className="text-sm font-semibold text-[#1f2a44]">{u.name || u.email}</div>
                        <div className="text-xs text-[#4b5563]">{u.email}</div>
                        <div className="mt-1 text-xs text-[#4b5563]">
                          Removed {fmtDate(u.deleted_at)}
                          {u.deleted_by ? ` by ${u.deleted_by}` : ""}
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => restoreUser(u.id, u.email)}
                        data-testid={`restore-user-${u.id}`}
                        className="rounded-sm border-[#1f2a44] text-[#1f2a44] hover:bg-[#1f2a44] hover:text-white h-9 px-4 text-xs uppercase tracking-[0.18em] font-semibold"
                      >
                        <ArchiveRestore className="h-3.5 w-3.5 mr-2" strokeWidth={1.75} />
                        Restore
                      </Button>
                    </div>
                  ))}
                </div>
                <div className="px-5 py-3 border-t border-[#e5e7eb] text-xs text-[#4b5563] leading-relaxed">
                  Removed accounts cannot sign in. Restoring one reinstates access
                  with its previous password and keeps all saved specifications.
                </div>
              </Card>
            )}
          </div>

          <div>
            <CreateUserForm onCreate={createUser} />
          </div>
        </div>
        )}
      </div>
    </div>
  );
}

function UserRow({ u, isSelf, onPatch, onDelete }) {
  const [resetOpen, setResetOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({ name: u.name || "", email: u.email, company: u.company || "" });

  const openEdit = () => {
    setForm({ name: u.name || "", email: u.email, company: u.company || "" });
    setEditOpen((v) => !v);
  };

  const saveEdit = async () => {
    const name = form.name.trim();
    const email = form.email.trim().toLowerCase();
    if (!name) return toast.error("Name is required");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return toast.error("Enter a valid email address");
    const payload = {};
    if (name !== (u.name || "")) payload.name = name;
    if (email !== u.email) payload.email = email;
    if (form.company.trim() !== (u.company || "")) payload.company = form.company.trim();
    if (Object.keys(payload).length === 0) {
      setEditOpen(false);
      return;
    }
    const ok = await onPatch(u.id, payload);
    if (ok !== false) setEditOpen(false);
  };

  return (
    <div className="p-4 sm:p-5 grid grid-cols-1 sm:grid-cols-12 gap-3 items-center" data-testid={`user-row-${u.id}`}>
      <div className="sm:col-span-5">
        <div className="text-sm font-semibold text-[#1f2a44]">{u.name || u.email}</div>
        <div className="text-xs text-[#4b5563]">{u.email}</div>
        {u.company && <div className="text-xs text-[#4b5563]">{u.company}</div>}
      </div>
      <div className="sm:col-span-3">
        <Select
          value={u.role}
          onValueChange={(v) => onPatch(u.id, { role: v })}
          disabled={isSelf}
        >
          <SelectTrigger data-testid={`user-role-${u.id}`} className="rounded-sm border-[#e5e7eb] bg-white text-xs h-8">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-sm bg-white">
            <SelectItem value="user">User</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="sm:col-span-2">
        <button
          type="button"
          onClick={() => onPatch(u.id, { is_active: !u.is_active })}
          data-testid={`user-active-${u.id}`}
          disabled={isSelf}
          className={`text-[11px] uppercase tracking-[0.18em] font-semibold px-2.5 py-1 rounded-sm border transition-colors ${
            u.is_active
              ? "border-[#1f2a44] text-[#1f2a44] hover:bg-[#f4f5f7]"
              : "border-[#c80f3f] text-[#c80f3f] hover:bg-[#c80f3f] hover:text-white"
          } ${isSelf ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          {u.is_active ? "Active" : "Inactive"}
        </button>
      </div>
      <div className="sm:col-span-2 flex items-center gap-2 justify-end">
        <button
          type="button"
          onClick={openEdit}
          data-testid={`user-edit-${u.id}`}
          className={`h-8 w-8 flex items-center justify-center border rounded-sm transition-colors ${
            editOpen
              ? "border-[#1f2a44] bg-[#1f2a44] text-white"
              : "border-[#e5e7eb] text-[#1f2a44] hover:border-[#1f2a44]"
          }`}
          title="Edit name, email and company"
        >
          <Pencil className="h-3.5 w-3.5" strokeWidth={1.75} />
        </button>
        <button
          type="button"
          onClick={() => setResetOpen((v) => !v)}
          data-testid={`user-reset-${u.id}`}
          className="h-8 w-8 flex items-center justify-center border border-[#e5e7eb] rounded-sm text-[#1f2a44] hover:border-[#1f2a44]"
          title="Reset password"
        >
          <KeyRound className="h-3.5 w-3.5" strokeWidth={1.75} />
        </button>
        <button
          type="button"
          onClick={() => onDelete(u.id, u.email)}
          disabled={isSelf}
          data-testid={`user-delete-${u.id}`}
          className="h-8 w-8 flex items-center justify-center border border-[#e5e7eb] rounded-sm text-[#c80f3f] hover:border-[#c80f3f] disabled:opacity-40 disabled:cursor-not-allowed"
          title="Remove user (restorable)"
        >
          <Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
        </button>
      </div>
      {editOpen && (
        <div
          data-testid={`user-edit-form-${u.id}`}
          className="sm:col-span-12 border-t border-[#e5e7eb] pt-4 mt-1 space-y-3"
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <EditField label="Name">
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid={`user-edit-name-${u.id}`}
                className="rounded-sm border-[#e5e7eb] h-9 text-sm"
              />
            </EditField>
            <EditField label="Email">
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid={`user-edit-email-${u.id}`}
                className="rounded-sm border-[#e5e7eb] h-9 text-sm"
              />
            </EditField>
            <EditField label="Company">
              <Input
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                data-testid={`user-edit-company-${u.id}`}
                className="rounded-sm border-[#e5e7eb] h-9 text-sm"
              />
            </EditField>
          </div>
          <div className="flex items-center gap-3">
            <Button
              type="button"
              onClick={saveEdit}
              data-testid={`user-edit-save-${u.id}`}
              className="bg-[#1f2a44] hover:bg-[#161e31] text-white rounded-sm h-9 text-xs px-4 uppercase tracking-[0.18em] font-semibold"
            >
              Save changes
            </Button>
            <button
              type="button"
              onClick={() => setEditOpen(false)}
              data-testid={`user-edit-cancel-${u.id}`}
              className="text-xs text-[#4b5563] hover:text-[#1f2a44] underline underline-offset-2"
            >
              Cancel
            </button>
            <span className="text-[11px] text-[#4b5563]">
              Changing the email changes how they sign in — their password stays the same.
            </span>
          </div>
        </div>
      )}
      {resetOpen && (
        <div className="sm:col-span-12 flex gap-2 items-center pt-2">
          <Input
            type="text"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder="New password (min 8 chars)"
            data-testid={`user-new-password-${u.id}`}
            className="rounded-sm border-[#e5e7eb] h-9 text-sm"
          />
          <Button
            onClick={() => {
              if (pw.length < 8) return toast.error("Min 8 characters");
              onPatch(u.id, { password: pw });
              setPw("");
              setResetOpen(false);
            }}
            data-testid={`user-save-password-${u.id}`}
            className="bg-[#1f2a44] hover:bg-[#161e31] text-white rounded-sm h-9 text-xs px-4"
          >
            Save
          </Button>
        </div>
      )}
    </div>
  );
}

function CreateUserForm({ onCreate }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("user");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    setBusy(true);
    await onCreate({ email: email.trim().toLowerCase(), name, company: company || undefined, password, role });
    setBusy(false);
    setEmail(""); setName(""); setCompany(""); setPassword(""); setRole("user");
  };

  return (
    <Card className="border border-[#e5e7eb] rounded-sm shadow-none">
      <div className="px-5 py-3 border-b border-[#e5e7eb] text-[11px] uppercase tracking-[0.22em] font-semibold text-[#4b5563]">
        Add user
      </div>
      <form onSubmit={submit} className="p-5 space-y-3">
        <Input placeholder="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="new-user-email" className="rounded-sm border-[#e5e7eb]" />
        <Input placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} required data-testid="new-user-name" className="rounded-sm border-[#e5e7eb]" />
        <Input placeholder="Company (optional)" value={company} onChange={(e) => setCompany(e.target.value)} data-testid="new-user-company" className="rounded-sm border-[#e5e7eb]" />
        <Input placeholder="Initial password (min 8)" type="text" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="new-user-password" className="rounded-sm border-[#e5e7eb]" />
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger data-testid="new-user-role" className="rounded-sm border-[#e5e7eb] bg-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-sm bg-white">
            <SelectItem value="user">User</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
          </SelectContent>
        </Select>
        <Button type="submit" disabled={busy} data-testid="new-user-submit" className="w-full bg-[#c80f3f] hover:bg-[#a10c32] text-white rounded-sm">
          {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Adding…</> : <><Plus className="mr-2 h-4 w-4" /> Add user</>}
        </Button>
      </form>
    </Card>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

function UsageTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/admin/usage")
      .then((r) => {
        if (!cancelled) setData(r.data);
      })
      .catch((e) =>
        toast.error("Failed to load usage", {
          description: formatApiErrorDetail(e?.response?.data?.detail),
        })
      )
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div data-testid="usage-loading" className="mt-10 flex items-center gap-2 text-sm text-[#4b5563]">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading usage…
      </div>
    );
  }
  if (!data) return null;

  const s = data.summary;
  const cards = [
    { label: "Users", value: s.total_users, sub: `${s.active_users} active` },
    { label: "Signed in (30d)", value: s.users_logged_in_30_days, sub: "unique users" },
    { label: "Specs generated", value: s.total_specs, sub: `${s.specs_last_30_days} in last 30 days` },
    { label: "Downloads", value: s.total_exports, sub: "PDF + Word" },
  ];

  return (
    <div data-testid="usage-tab" className="mt-8">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div
            key={c.label}
            data-testid={`usage-card-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className="border border-[#e5e7eb] rounded-sm p-5"
          >
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#4b5563]">
              {c.label}
            </div>
            <div className="mt-2 font-display text-3xl font-bold text-[#1f2a44]">{c.value}</div>
            <div className="mt-1 text-xs text-[#4b5563]">{c.sub}</div>
          </div>
        ))}
      </div>

      {s.segment_totals?.length > 0 && (
        <div className="mt-6 flex flex-wrap gap-2" data-testid="usage-segment-totals">
          {s.segment_totals.map((t) => (
            <span
              key={t.segment}
              className="text-xs border border-[#e5e7eb] rounded-sm px-3 py-1.5 text-[#1f2a44]"
            >
              {t.segment}
              <span className="ml-2 font-semibold text-[#c80f3f]">{t.count}</span>
            </span>
          ))}
        </div>
      )}

      <Card className="mt-8 border border-[#e5e7eb] rounded-sm shadow-none">
        <div className="px-5 py-3 border-b border-[#e5e7eb] flex items-center justify-between gap-4 flex-wrap">
          <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#4b5563]">
            Usage by user ({data.users.length})
          </span>
          <span className="text-[11px] text-[#4b5563]">
            Clause library V{s.library_version} · {s.library_version_date}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="usage-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.16em] text-[#4b5563] border-b border-[#e5e7eb]">
                <th className="px-4 py-3 font-semibold">User</th>
                <th className="px-3 py-3 font-semibold">Role</th>
                <th className="px-3 py-3 font-semibold">Last login</th>
                <th className="px-3 py-3 font-semibold">Logins</th>
                <th className="px-3 py-3 font-semibold">Specs</th>
                <th className="px-3 py-3 font-semibold">30d</th>
                <th className="px-3 py-3 font-semibold">Last spec</th>
                <th className="px-3 py-3 font-semibold">Top segment</th>
                <th className="px-3 py-3 font-semibold">PDF</th>
                <th className="px-3 py-3 font-semibold">Word</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#e5e7eb]">
              {data.users.map((u) => (
                <tr key={u.id} data-testid={`usage-row-${u.id}`} className="align-top">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-[#1f2a44]">{u.name || u.email}</div>
                    <div className="text-xs text-[#4b5563]">{u.email}</div>
                    {u.company && <div className="text-xs text-[#4b5563]">{u.company}</div>}
                    {!u.is_active && (
                      <div className="mt-1 text-[10px] uppercase tracking-[0.16em] font-semibold text-[#c80f3f]">
                        Inactive
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-3 text-xs text-[#4b5563] capitalize">{u.role}</td>
                  <td className="px-3 py-3 text-xs text-[#4b5563]">{fmtDate(u.last_login_at)}</td>
                  <td className="px-3 py-3 text-xs text-[#1f2a44] font-semibold">{u.login_count}</td>
                  <td className="px-3 py-3 text-xs text-[#1f2a44] font-semibold">{u.specs_total}</td>
                  <td className="px-3 py-3 text-xs text-[#4b5563]">{u.specs_last_30_days}</td>
                  <td className="px-3 py-3 text-xs text-[#4b5563]">{fmtDate(u.last_spec_at)}</td>
                  <td className="px-3 py-3 text-xs text-[#4b5563]">{u.top_segment || "—"}</td>
                  <td className="px-3 py-3 text-xs text-[#4b5563]">{u.exports_pdf}</td>
                  <td className="px-3 py-3 text-xs text-[#4b5563]">{u.exports_docx}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      <p className="mt-4 text-xs text-[#4b5563] leading-relaxed">
        Login and download counters started when usage tracking was enabled;
        specification history is complete for all time.
      </p>
    </div>
  );
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function RequestsTab({ onCount }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/auth/password-requests");
      setRows(r.data.requests || []);
      onCount?.(r.data.open_count || 0);
    } catch (e) {
      toast.error("Failed to load requests", {
        description: formatApiErrorDetail(e?.response?.data?.detail),
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markActioned = async (id) => {
    try {
      await api.post(`/auth/password-requests/${id}/actioned`);
      toast.success("Marked as actioned");
      load();
    } catch (e) {
      toast.error("Update failed", {
        description: formatApiErrorDetail(e?.response?.data?.detail),
      });
    }
  };

  if (loading) {
    return (
      <div data-testid="requests-loading" className="mt-10 flex items-center gap-2 text-sm text-[#4b5563]">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading requests…
      </div>
    );
  }

  return (
    <div data-testid="requests-tab" className="mt-8">
      <p className="text-sm text-[#4b5563] leading-relaxed max-w-2xl">
        Sign-in help submitted from the login page. Each request is emailed to
        marketing@helvar.com. To action one: reset the password from Team &amp;
        access (or add the account), confirm it with the person directly, then
        mark it as actioned here.
      </p>

      <Card className="mt-6 border border-[#e5e7eb] rounded-sm shadow-none">
        <div className="px-5 py-3 border-b border-[#e5e7eb] text-[11px] uppercase tracking-[0.22em] font-semibold text-[#4b5563]">
          Requests ({rows.length})
        </div>
        <div className="divide-y divide-[#e5e7eb]">
          {rows.map((r) => (
            <div
              key={r.id}
              data-testid={`request-row-${r.id}`}
              className="px-5 py-4 flex items-start justify-between gap-4 flex-wrap"
            >
              <div className="min-w-0">
                <div className="text-sm font-semibold text-[#1f2a44] break-all">{r.email}</div>
                <div className="mt-1 text-xs text-[#4b5563]">
                  {r.account_active
                    ? "Has an active account — needs a password reset"
                    : r.has_account
                    ? "Account exists but is inactive or removed"
                    : "No account yet — access request"}
                </div>
                {r.message && (
                  <div className="mt-2 text-xs text-[#1f2937] italic">“{r.message}”</div>
                )}
                <div className="mt-2 text-[11px] text-[#4b5563]">
                  {fmtDateTime(r.requested_at)}
                  {r.notified === false && " · email notification failed"}
                  {r.status === "actioned" &&
                    ` · actioned ${fmtDateTime(r.actioned_at)}${r.actioned_by ? ` by ${r.actioned_by}` : ""}`}
                </div>
              </div>
              {r.status === "open" ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => markActioned(r.id)}
                  data-testid={`request-actioned-${r.id}`}
                  className="rounded-sm border-[#1f2a44] text-[#1f2a44] hover:bg-[#1f2a44] hover:text-white h-9 px-4 text-xs uppercase tracking-[0.18em] font-semibold"
                >
                  <Check className="h-3.5 w-3.5 mr-2" strokeWidth={2} />
                  Mark actioned
                </Button>
              ) : (
                <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#4b5563] border border-[#d1d5db] bg-[#f4f5f7] px-3 py-1.5 rounded-sm">
                  Actioned
                </span>
              )}
            </div>
          ))}
          {rows.length === 0 && (
            <div className="p-6 text-sm text-[#4b5563] italic">No requests yet.</div>
          )}
        </div>
      </Card>
    </div>
  );
}

function EditField({ label, children }) {
  return (
    <div className="space-y-1.5">
      <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#4b5563]">
        {label}
      </div>
      {children}
    </div>
  );
}
